import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, Plus, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const TYPE_LABEL = {
  equity_research: "Equity research",
  private_credit_memo: "Private credit",
  pe_investment_memo: "PE / VC memo",
  quarterly_portfolio_update: "Quarterly portfolio update",
};

const statusColor = (s) => {
  if (s === "completed") return "text-[var(--ink)]";
  if (s === "failed") return "text-[#a33]";
  return "text-[var(--saffron)]";
};

export default function Dashboard() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [docs, setDocs] = useState([]);

  const load = async () => {
    const [r, d] = await Promise.all([api.get("/reports"), api.get("/documents")]);
    setReports(r.data);
    setDocs(d.data);
  };

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);

  const completed = reports.filter((r) => r.status === "completed").length;
  const generating = reports.filter((r) => r.status === "generating").length;

  return (
    <div className="max-w-[1100px] mx-auto px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)] border-b border-[var(--ink)]/15 pb-3">
        <span>Workspace · Overview</span>
        <span>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
      </div>

      <div className="mt-12 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="smallcaps text-[var(--saffron)]">No. 01 — Overview</div>
          <h1 className="font-serif text-[48px] font-medium tracking-[-0.02em] mt-4" data-testid="dashboard-greeting">
            Good to see you,
            <br />
            {user?.full_name?.split(" ")[0] || "analyst"}.
          </h1>
        </div>
        <Link
          to="/app/reports/new"
          className="inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] px-6 py-3 text-[13px] tracking-wide transition-colors duration-200"
          data-testid="dashboard-new-report"
        >
          <Plus className="w-3.5 h-3.5" /> New report
        </Link>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--ink)]/15">
        {[
          { label: "Reports drafted", value: completed },
          { label: "In progress", value: generating },
          { label: "Documents", value: docs.length },
        ].map((s, i) => (
          <div key={i} className="bg-[var(--cream)] p-7" data-testid={`stat-${i}`}>
            <div className="smallcaps text-[var(--warm-grey)]">{s.label}</div>
            <div className="font-serif text-[52px] font-medium mt-4 tabular leading-none">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-16 flex items-end justify-between border-b border-[var(--ink)]/15 pb-3">
        <h2 className="font-serif text-[28px] font-medium tracking-[-0.015em]">Recent reports</h2>
        <Link to="/app/reports" className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)] hover:text-[var(--ink)] flex items-center gap-2 transition-colors" data-testid="dashboard-view-all-reports">
          All reports <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="mt-2 stagger">
        {reports.length === 0 && (
          <div className="bg-white border border-[var(--ink)]/15 p-12 text-center" data-testid="dashboard-empty-state">
            <div className="font-serif text-[24px] font-medium">No reports yet</div>
            <p className="text-[14px] text-[var(--ink)]/70 mt-3 max-w-md mx-auto">Upload a quarterly MIS pack and produce your first investment committee memo.</p>
            <Link to="/app/reports/new" className="mt-7 inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] px-6 py-3 text-[13px] tracking-wide">
              <Plus className="w-3.5 h-3.5" /> Start a report
            </Link>
          </div>
        )}
        {reports.slice(0, 8).map((r) => {
          const StatusIcon = r.status === "completed" ? CheckCircle2 : r.status === "failed" ? XCircle : Loader2;
          return (
            <Link
              key={r.id}
              to={`/app/reports/${r.id}`}
              className="grid grid-cols-12 items-baseline gap-4 py-4 border-b border-[var(--ink)]/10 hover:bg-white transition-colors"
              data-testid={`dashboard-report-row-${r.id}`}
            >
              <div className="col-span-1 font-mono text-[10px] text-[var(--saffron)] tabular">{`0${reports.indexOf(r) + 1}`.slice(-2)}</div>
              <div className="col-span-6">
                <div className="font-serif text-[18px] font-medium">{r.company_name}</div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--warm-grey)] mt-1">{TYPE_LABEL[r.report_type]}</div>
              </div>
              <div className="col-span-3 text-[12px] text-[var(--warm-grey)] tabular">{new Date(r.created_at).toLocaleDateString("en-IN")}</div>
              <div className={`col-span-2 text-right text-[11px] tracking-[0.18em] uppercase font-semibold ${statusColor(r.status)} flex items-center justify-end gap-1.5`}>
                <StatusIcon className={`w-3.5 h-3.5 ${r.status === "generating" ? "animate-spin" : ""}`} />
                {r.status}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
